package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.events.Event
import kotlin.math.PI
import kotlin.math.max
import kotlin.math.min
import kotlin.math.sin

private const val HIGH_SCORE_KEY = "flappyCrescentHighScore"

// Colors (from SiteTheme)
private const val TEAL = "#0E7C7B"
private const val TEAL_LIGHT = "#14A3A0"
private const val GOLD = "#C8962E"
private const val GOLD_DARK = "#9A7220"

// Game constants
private const val CANVAS_HEIGHT = 300.0
private const val GRAVITY = 0.25
private const val FLAP_VELOCITY = -5.0
private const val BIRD_RADIUS = 16.0 // hit box

// The bird (/animations/bird_flapping.json, a LottieFiles free animation, the same file as mobile)
// is drawn in a square this big. It fills about half of its 300x300 frame, centred at these points.
private const val BIRD_FRAME = 86.0
private const val BIRD_CENTER_IN_FRAME_X = 0.49
private const val BIRD_CENTER_IN_FRAME_Y = 0.53
private const val BIRD_CANVAS_PX = 160 // lottie-web draws into this, then it's copied (sharp at 2x)
private const val BIRD_FPS = 30.0
private const val PILLAR_WIDTH = 48.0
private const val GAP_SIZE = 140.0
private const val PILLAR_SPACING = 250.0
private const val BASE_SPEED = 2.2
private const val SPEED_INCREMENT = 0.02
private const val MAX_SPEED = 4.0
private const val DOME_RADIUS = 16.0
private const val MAX_VELOCITY = 5.5

private enum class GameState { READY, PLAYING, GAME_OVER }

private data class Pillar(
    var x: Double,
    val gapY: Double, // center of the gap
    var scored: Boolean = false
)

@Composable
fun FlappyCrescentGame() {
    Div({
        style {
            width(100.percent)
            position(Position.Relative)
            property("user-select", "none")
            property("-webkit-user-select", "none")
            property("-webkit-tap-highlight-color", "transparent")
            cursor("pointer")
        }
        attr("id", "flappy-crescent-wrapper")
    }) {
        // The canvas element rendered as raw HTML since Compose HTML doesn't have a Canvas component
        DisposableEffect(Unit) {
            val wrapper = document.getElementById("flappy-crescent-wrapper") ?: return@DisposableEffect onDispose {}

            // Create canvas element
            val canvas = document.createElement("canvas") as HTMLCanvasElement
            canvas.id = "flappy-crescent-canvas"
            canvas.style.width = "100%"
            canvas.style.height = "${CANVAS_HEIGHT.toInt()}px"
            canvas.style.display = "block"
            canvas.style.borderRadius = "12px"
            wrapper.appendChild(canvas)

            // lottie-web draws the bird's current frame into its own small canvas, which is copied onto
            // the game canvas each frame (so the Game Over veil still covers it). A plain gold dot stands
            // in until it has loaded. The sky's sprites (FlappySky.kt) work the same way.
            val sky = FlappySky()
            val birdCanvas = document.createElement("canvas") as HTMLCanvasElement
            birdCanvas.width = BIRD_CANVAS_PX
            birdCanvas.height = BIRD_CANVAS_PX
            var bird: dynamic = null
            var disposed = false
            fun initLottie() {
                val lottie = window.asDynamic().lottie
                if (disposed || bird != null || lottie == null || lottie == undefined) return
                sky.load(lottie)
                val settings: dynamic = js("({})")
                settings.context = birdCanvas.getContext("2d")
                settings.clearCanvas = true
                val options: dynamic = js("({})")
                options.renderer = "canvas"
                options.loop = true
                options.autoplay = false
                options.path = "/animations/bird_flapping.json"
                options.rendererSettings = settings
                bird = lottie.loadAnimation(options)
            }
            val lottieScript = document.querySelector("script[src*='lottie-web']")
            if (window.asDynamic().lottie != null && window.asDynamic().lottie != undefined) {
                initLottie()
            } else if (lottieScript != null) {
                lottieScript.addEventListener("load", { initLottie() })
            } else {
                val script = document.createElement("script")
                script.asDynamic().src = "https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js"
                script.addEventListener("load", { initLottie() })
                document.head?.appendChild(script)
            }

            // Game state variables
            var state = GameState.READY
            var birdY = CANVAS_HEIGHT / 2
            var velocity = 0.0
            var score = 0
            var highScore = localStorage.getItem(HIGH_SCORE_KEY)?.toIntOrNull() ?: 0
            var pillars = mutableListOf<Pillar>()
            var speed = BASE_SPEED
            var canvasWidth = 400.0
            var animFrameId = 0
            var lastTime = 0.0
            var birdRotation = 0.0
            var gameOverTimer = 0.0 // prevent instant restart
            var skyPos = 0.0 // the sky's phase, following the score (see FlappySky)
            var skyScroll = 0.0 // px the world has moved, for the sky's parallax

            fun getCanvasWidth(): Double {
                val rect = canvas.getBoundingClientRect()
                return rect.width
            }

            fun resetGame() {
                canvasWidth = getCanvasWidth()
                birdY = CANVAS_HEIGHT / 2
                velocity = 0.0
                score = 0
                speed = BASE_SPEED
                birdRotation = 0.0
                gameOverTimer = 0.0
                skyPos = 0.0
                pillars.clear()
                // Seed initial pillars
                val cw = canvasWidth
                var x = cw + 100.0
                while (x < cw + 600.0) {
                    val margin = GAP_SIZE / 2 + 30.0
                    val gapY = margin + (CANVAS_HEIGHT - 2 * margin) * js("Math.random()").unsafeCast<Double>()
                    pillars.add(Pillar(x, gapY))
                    x += PILLAR_SPACING
                }
            }

            fun drawBird(ctx: CanvasRenderingContext2D, x: Double, y: Double, rotation: Double, timestamp: Double) {
                ctx.save()
                ctx.translate(x, y)
                ctx.rotate(rotation)
                val anim = bird
                if (anim != null && anim.isLoaded == true) {
                    val frames = (anim.totalFrames as Number).toDouble()
                    anim.goToAndStop((timestamp / 1000.0 * BIRD_FPS) % frames, true)
                    ctx.drawImage(birdCanvas, -BIRD_FRAME * BIRD_CENTER_IN_FRAME_X, -BIRD_FRAME * BIRD_CENTER_IN_FRAME_Y, BIRD_FRAME, BIRD_FRAME)
                } else {
                    ctx.beginPath()
                    ctx.arc(0.0, 0.0, BIRD_RADIUS * 0.8, 0.0, 2 * PI)
                    ctx.fillStyle = GOLD
                    ctx.fill()
                }
                ctx.restore()
            }

            fun drawMinaret(ctx: CanvasRenderingContext2D, x: Double, topHeight: Double, bottomY: Double, cw: Double) {
                val ch = CANVAS_HEIGHT

                // Top pillar
                if (topHeight > 0) {
                    // Main shaft
                    ctx.fillStyle = TEAL
                    ctx.fillRect(x, 0.0, PILLAR_WIDTH, topHeight - DOME_RADIUS)

                    // Dome cap at bottom of top pillar
                    ctx.beginPath()
                    ctx.arc(
                        x + PILLAR_WIDTH / 2,
                        topHeight - DOME_RADIUS,
                        PILLAR_WIDTH / 2,
                        0.0,
                        PI
                    )
                    ctx.fill()

                    // Decorative band
                    ctx.fillStyle = TEAL_LIGHT
                    val bandY = max(0.0, topHeight - DOME_RADIUS - 8.0)
                    ctx.fillRect(x + 4, bandY, PILLAR_WIDTH - 8, 6.0)
                }

                // Bottom pillar
                if (bottomY < ch) {
                    // Dome cap at top of bottom pillar
                    ctx.fillStyle = TEAL
                    ctx.beginPath()
                    ctx.arc(
                        x + PILLAR_WIDTH / 2,
                        bottomY + DOME_RADIUS,
                        PILLAR_WIDTH / 2,
                        PI,
                        2 * PI
                    )
                    ctx.fill()

                    // Main shaft
                    ctx.fillRect(x, bottomY + DOME_RADIUS, PILLAR_WIDTH, ch - bottomY - DOME_RADIUS)

                    // Decorative band
                    ctx.fillStyle = TEAL_LIGHT
                    ctx.fillRect(x + 4, bottomY + DOME_RADIUS + 2, PILLAR_WIDTH - 8, 6.0)
                }
            }

            fun drawText(
                ctx: CanvasRenderingContext2D,
                text: String,
                x: Double,
                y: Double,
                size: Int,
                color: String = TEAL,
                align: String = "center",
                shadow: Double = 0.15
            ) {
                ctx.font = "bold ${size}px 'Segoe UI', system-ui, sans-serif"
                ctx.asDynamic().textAlign = align
                ctx.asDynamic().textBaseline = "middle"
                // Shadow for readability
                ctx.shadowColor = "rgba(0,0,0,$shadow)"
                ctx.shadowBlur = 4.0
                ctx.shadowOffsetX = 1.0
                ctx.shadowOffsetY = 1.0
                ctx.fillStyle = color
                ctx.fillText(text, x, y)
                ctx.shadowBlur = 0.0
                ctx.shadowOffsetX = 0.0
                ctx.shadowOffsetY = 0.0
            }

            fun render(ctx: CanvasRenderingContext2D, cw: Double, timestamp: Double) {
                val ch = CANVAS_HEIGHT
                val dpr = window.devicePixelRatio
                canvas.width = (cw * dpr).toInt()
                canvas.height = (ch * dpr).toInt()
                ctx.scale(dpr, dpr)

                sky.draw(ctx, cw, ch, skyPos, skyScroll, timestamp / 1000.0)

                // Pillars
                val birdX = cw * 0.25

                for (pillar in pillars) {
                    val topH = pillar.gapY - GAP_SIZE / 2
                    val bottomY = pillar.gapY + GAP_SIZE / 2
                    drawMinaret(ctx, pillar.x, topH, bottomY, cw)
                }

                // Bird: nose up when it flaps, down (not too far) when it falls
                if (state != GameState.READY) {
                    birdRotation = min(max(velocity * 0.08, -0.45), 0.6)
                }
                drawBird(ctx, birdX, birdY, birdRotation, timestamp)

                // Score display
                if (state == GameState.PLAYING) {
                    drawText(ctx, "$score", cw / 2, 36.0, 32, "#FFFFFF", shadow = 0.35)
                }

                // Ready screen
                if (state == GameState.READY) {
                    // A light veil so the text reads on the sky
                    ctx.fillStyle = "rgba(255, 255, 255, 0.45)"
                    ctx.fillRect(0.0, 0.0, cw, ch)

                    drawText(ctx, "While you wait...", cw / 2, ch / 2 - 60, 14, TEAL_LIGHT)
                    drawText(ctx, "Fly, Little Bird!", cw / 2, ch / 2 - 30, 26, TEAL)
                    drawText(ctx, "Tap to Start", cw / 2, ch / 2 + 15, 18, GOLD)

                    if (highScore > 0) {
                        drawText(ctx, "Best: $highScore", cw / 2, ch / 2 + 50, 14, TEAL_LIGHT)
                    }
                }

                // Game over screen
                if (state == GameState.GAME_OVER) {
                    ctx.fillStyle = "rgba(255, 255, 255, 0.7)"
                    ctx.fillRect(0.0, 0.0, cw, ch)

                    drawText(ctx, "Game Over", cw / 2, ch / 2 - 45, 26, TEAL)
                    drawText(ctx, "Score: $score", cw / 2, ch / 2 - 10, 22, GOLD)
                    drawText(ctx, "Best: $highScore", cw / 2, ch / 2 + 22, 16, TEAL_LIGHT)
                    drawText(ctx, "Tap to Retry", cw / 2, ch / 2 + 55, 16, GOLD_DARK)
                }
            }

            fun gameLoop(timestamp: Double) {
                val cw = getCanvasWidth()
                if (cw <= 0) {
                    animFrameId = window.requestAnimationFrame { gameLoop(it) }
                    return
                }

                val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D
                if (ctx == null) {
                    animFrameId = window.requestAnimationFrame { gameLoop(it) }
                    return
                }

                // Delta time (cap at 33ms to handle tab switches)
                val dt = if (lastTime == 0.0) 16.0 else min(timestamp - lastTime, 33.0)
                lastTime = timestamp
                val dtFactor = dt / 16.0 // normalize to ~60fps

                if (state == GameState.PLAYING) {
                    // Physics
                    velocity += GRAVITY * dtFactor
                    velocity = min(velocity, MAX_VELOCITY)
                    birdY += velocity * dtFactor

                    // Move pillars
                    val currentSpeed = speed * dtFactor
                    val birdX = cw * 0.25

                    for (pillar in pillars) {
                        pillar.x -= currentSpeed
                    }
                    skyScroll += currentSpeed

                    // Remove off-screen pillars and add new ones
                    pillars.removeAll { it.x + PILLAR_WIDTH < 0 }

                    val lastPillarX = pillars.maxOfOrNull { it.x } ?: 0.0
                    if (lastPillarX < cw) {
                        val margin = GAP_SIZE / 2 + 30.0
                        val gapY = margin + (CANVAS_HEIGHT - 2 * margin) * js("Math.random()").unsafeCast<Double>()
                        pillars.add(Pillar(lastPillarX + PILLAR_SPACING, gapY))
                    }

                    // Score
                    for (pillar in pillars) {
                        if (!pillar.scored && pillar.x + PILLAR_WIDTH < birdX) {
                            pillar.scored = true
                            score++
                            speed = min(BASE_SPEED + score * SPEED_INCREMENT, MAX_SPEED)
                        }
                    }

                    // Collision detection
                    val birdTop = birdY - BIRD_RADIUS
                    val birdBottom = birdY + BIRD_RADIUS
                    val birdLeft = birdX - BIRD_RADIUS
                    val birdRight = birdX + BIRD_RADIUS

                    // Floor/ceiling
                    if (birdBottom > CANVAS_HEIGHT || birdTop < 0) {
                        state = GameState.GAME_OVER
                    }

                    // Pillar collision
                    for (pillar in pillars) {
                        val pLeft = pillar.x
                        val pRight = pillar.x + PILLAR_WIDTH
                        val gapTop = pillar.gapY - GAP_SIZE / 2
                        val gapBottom = pillar.gapY + GAP_SIZE / 2

                        if (birdRight > pLeft + 8 && birdLeft < pRight - 8) {
                            if (birdTop < gapTop - 4 || birdBottom > gapBottom + 4) {
                                state = GameState.GAME_OVER
                            }
                        }
                    }

                    if (state == GameState.GAME_OVER) {
                        if (score > highScore) {
                            highScore = score
                            localStorage.setItem(HIGH_SCORE_KEY, highScore.toString())
                        }
                        gameOverTimer = 0.0
                    }
                }

                if (state == GameState.GAME_OVER) {
                    gameOverTimer += dt
                }

                // Gentle floating animation on ready screen
                if (state == GameState.READY) {
                    birdY = CANVAS_HEIGHT / 2 + sin(timestamp / 500.0) * 12.0
                    skyScroll += SKY_IDLE_DRIFT * dtFactor
                }

                // The sky blends toward the phase the score has reached
                val skyStep = SKY_BLEND_SPEED * dt / 1000.0
                skyPos += (score.toDouble() / SKY_POINTS_PER_PHASE - skyPos).coerceIn(-skyStep, skyStep)

                render(ctx, cw, timestamp)
                animFrameId = window.requestAnimationFrame { gameLoop(it) }
            }

            fun handleInput(@Suppress("UNUSED_PARAMETER") e: Event) {
                when (state) {
                    GameState.READY -> {
                        resetGame()
                        state = GameState.PLAYING
                        velocity = FLAP_VELOCITY
                    }
                    GameState.PLAYING -> {
                        velocity = FLAP_VELOCITY
                    }
                    GameState.GAME_OVER -> {
                        // Require 400ms delay before retry to avoid accidental restarts
                        if (gameOverTimer > 400) {
                            resetGame()
                            state = GameState.PLAYING
                            velocity = FLAP_VELOCITY
                        }
                    }
                }
            }

            // Prevent default on touch to avoid scrolling
            val touchHandler: (Event) -> Unit = { e ->
                e.preventDefault()
                handleInput(e)
            }
            val clickHandler: (Event) -> Unit = { e -> handleInput(e) }

            canvas.addEventListener("click", clickHandler)
            canvas.addEventListener("touchstart", touchHandler)

            // Initialize
            resetGame()
            state = GameState.READY
            animFrameId = window.requestAnimationFrame { gameLoop(it) }

            onDispose {
                disposed = true
                bird?.destroy()
                sky.destroy()
                window.cancelAnimationFrame(animFrameId)
                canvas.removeEventListener("click", clickHandler)
                canvas.removeEventListener("touchstart", touchHandler)
                canvas.parentElement?.removeChild(canvas)
            }
        }
    }
}
